import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { isDocOwner, isDocUnsigned } from "../../helpers/docPermissions";
import { useContainerDimensions } from "../../hooks";

import { DIIA_RESET, signDocs as diiaSign } from "../../actions/diia";
import { DOCUMENT_VIEW_SUCCESS, fetchDocument } from "../../actions/view";
import { SIGN_RESET, signDocs } from "../../actions/sign";

import styles from "../../styles/View/View.module.css";
import { BtnWrapper } from "../../styles/Docs/DocsToolbar.styled";
import stylesDoc from "../../styles/Spreadsheet/Spreadsheet.module.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import { Edit, EmptyDocForPdfViewer, ZoomIcon } from "../../../public/icons";
import Tooltip from "@mui/material/Tooltip";
import ScaleSlider from "../ScaleSlider";

import DocsToolbarDevice from "../Docs/DocsToolbarDevice";
import PdfViewer from "./PdfViewer";
import { LinkIcon } from "../../../public/icons";
import { SkeletonInfobar, SkeletonOneLine } from "../Loaders/SkeletonLoader";
import { SignPopup } from "../Popup/SignPopup";
import { DiiaSignPopup } from "../Popup/DiiaSignPopup";
import Infobar from "./Infobar";
import { FileSignPopup } from "../Popup/FileSignPopup";
import DownloadButton from "../DownloadButton";
import { StatusDocInfo } from "./Infobar";
import AdminDocCounterparties from "./CounterpartiesBtn";
import ArchiveButton from "./ArchiveButton";
import { ARCHIVE_RESET } from "../../actions/archive";
import { AddAttributesDocPopup } from "../attributes/AddAttributesDocPopup";
import { DOC_EDIT_ATTRIBUTE_RESET } from "../../actions/attributes";
import { AlertTooltip } from "../Spreadsheet/ModTooltip";
import { SIGNER_ADD_RESET, SIGNER_DELETE_RESET } from "../../actions/signers";
import DeleteButton from "../docActions/DeleteButton";
import { DeleteConfirmModal } from "../Popup/ConfirmModal";
import { useDeleteDocs } from "../../hooks/useDeleteDocs";

export default function View() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const diia = useAppSelector((state) => state.diia);
  const sign = useAppSelector((state) => state.sign);
  const doc = useAppSelector((state) => state.view);
  const archive = useAppSelector((state) => state.archive);
  const [showPopup, setShowPopup] = useState(false);
  const [showFilePopup, setShowFilePopup] = useState(false);
  const [showDiiaPopup, setShowDiiaPopup] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const [scale, setScale] = useState(65);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  const goTo = (page: number) => {
    if (page >= 1 && page <= numPages) setCurrentPage(page);
  };
  const [copyUrl, setCopyUrl] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [editAttrPopup, setEditAttrPopup] = useState(false);
  const [attrHint, setAttrHint] = useState<string | undefined>();
  const editDocAttributes = useAppSelector((state) => state.editDocAttributes);
  const signerAddState = useAppSelector((state) => state.signerAdd);
  const deleteDocState = useAppSelector((state) => state.deleteDoc);
  const signerDeleteState = useAppSelector((state) => state.signerDelete);

  const adminDoc = doc.public && doc.creator?.id === user.id;
  const viewLink =
    user.permissions["pub.docs.upload"] &&
    doc.signatures?.length === 0 &&
    adminDoc;
  const componentRef = useRef<HTMLDivElement>(null);
  const { width, height } = useContainerDimensions(componentRef);

  const signed =
    doc.signatures?.filter((el: { stamp: boolean }) => !el.stamp).length > 0 &&
    (doc.signatures?.filter((el: { stamp: boolean }) => el.stamp).length > 0 ||
      !user.company?.stamp);
  const isDesktop = useMediaQuery({ minWidth: 1240 });
  const isMobile = useMediaQuery({ maxWidth: 1239 });
  const isNarrow = useMediaQuery({ maxWidth: 799 });

  const {
    showConfirmModal,
    docsToDeleteTitles,
    errorDelete,
    deleteState,
    canDeleteDoc,
    initiateDelete,
    confirmDelete,
    cancelDelete,
  } = useDeleteDocs(user, { navigateBack: true });

  const canDelete = canDeleteDoc(doc);
  const canEditAttributes = isDocOwner(doc, user.id) && isDocUnsigned(doc);

  const onClickCopy = () => {
    setCopyUrl(true);
    setTooltipOpen(true);
    setTimeout(() => setTooltipOpen(false), 1500);

    const pubLink = window.location.origin + "/pub" + window.location.pathname;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(pubLink);
    }
    setTimeout(() => setCopyUrl(false), 3000);
  };

  const onHoverCopy = () => {
    if (!isMobile) {
      setTooltipOpen(true);
    }
  };
  const onCloseCopy = () => {
    if (!isMobile && !copyUrl) {
      setTooltipOpen(false);
    }
  };

  const handleSliderChange = (event: Event, newValue: number) => {
    setScale(newValue);
  };

  const clickBackButton = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (showDetails) {
        setShowDetails(false);
      } else {
        navigate(-1);
      }
    },
    [showDetails],
  );

  const clickCloseButton = useCallback(() => {
    setShowDetails(false);
  }, [showDetails]);

  const closePopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  const closeDiiaPopup = useCallback(() => {
    dispatch({ type: DIIA_RESET });
    setShowDiiaPopup(false);
  }, []);

  const closeFilePopup = useCallback(() => {
    setShowFilePopup(false);
  }, []);

  const clickSignButton = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: SIGN_RESET });
    setShowPopup(true);
  }, []);

  const clickDiiaSignButton = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: SIGN_RESET });
    dispatch(diiaSign([params.id]));
    setShowDiiaPopup(true);
  }, []);

  const clickFileSignButton = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: SIGN_RESET });
    setShowFilePopup(true);
  }, []);

  const signMethods =
    !signed && doc.methods
      ? doc.methods.reduce((memo: Record<string, any>, method: { alias: string }) => {
          switch (method.alias) {
            case "file":
              memo.file = clickFileSignButton;
              break;
            case "diia":
              memo.diia = clickDiiaSignButton;
              break;
            case "gryada":
              memo.gryada = clickSignButton;
              break;
            case "stamp":
              memo.stamp = clickFileSignButton;
              break;
            default:
              memo[method.alias] = null;
          }
          return memo;
        }, {})
      : {};

  const submitForm = useCallback(
    (key: any) => {
      dispatch(signDocs([params.id ?? ""], key, false));
    },
    [dispatch, params.id],
  );

  useEffect(() => {
    dispatch(fetchDocument(params.id));
  }, [params.id]);

  useEffect(() => {
    if (sign.state === "success") {
      dispatch({ type: SIGN_RESET });
      const result = sign.list.reduce((memo: any, el: any) => {
        memo[el.id] = { ...el };
        return memo;
      }, {});
      dispatch({
        type: DOCUMENT_VIEW_SUCCESS,
        payload: result[params.id],
      });
      closeFilePopup();
      closeDiiaPopup();
      closePopup();
    }
  }, [sign, params.id]);

  useEffect(() => {
    if (archive.state === "success") {
      dispatch(fetchDocument(params.id));
      const timer = setTimeout(() => {
        dispatch({ type: ARCHIVE_RESET });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [archive.state, params.id, dispatch]);

  useEffect(() => {
    if (editDocAttributes.state === "success") {
      dispatch(fetchDocument(params.id));
    }
    dispatch({ type: DOC_EDIT_ATTRIBUTE_RESET });
  }, [editDocAttributes.state, doc.id, dispatch]);

  useEffect(() => {
    if (
      signerAddState.state === "success" ||
      signerDeleteState.state === "success"
    ) {
      dispatch(fetchDocument(params.id));
    }
    dispatch({ type: SIGNER_ADD_RESET });
    dispatch({ type: SIGNER_DELETE_RESET });
  }, [signerAddState.state, doc.id, dispatch, signerDeleteState.state]);

  if (doc.state === "failure") {
    return (
      <section className={isMobile ? styles.boxmobile : styles.box}>
        <div className={stylesDoc.noDocs}>
          <div style={{ textAlign: "center" }}>
            <EmptyDocForPdfViewer />
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className={isMobile ? styles.boxmobile : styles.box}>
        {isMobile && showDetails && (
          <Infobar
            doc={doc}
            clickCloseButton={clickCloseButton}
            sign={sign}
            heightBar={Object.keys(signMethods).length}
            signMethods={signMethods}
          />
        )}
        {!showDetails && (
          <div className={styles.left}>
            <div className={styles.toolsLine}>
              <AlertTooltip title="Назад">
                <BtnWrapper
                  $bgColor="var(--sidebar-hover)"
                  onClick={clickBackButton}
                >
                  <ArrowBackIosNewIcon
                    sx={{ color: "var(--blue)", fontSize: 16 }}
                  />
                </BtnWrapper>
              </AlertTooltip>
              {doc.state === "success" && (
                <>
                  {canEditAttributes && (
                    <AlertTooltip title="Редагування параметрів">
                      <BtnWrapper
                        $bgColor="var(--sidebar-hover)"
                        onClick={() => {
                          setEditAttrPopup(true);
                        }}
                      >
                        <Edit color={"var(--blue)"} />
                      </BtnWrapper>
                    </AlertTooltip>
                  )}

                  {viewLink && (
                    <Tooltip
                      open={tooltipOpen}
                      title={copyUrl ? "Скопійовано" : "Скопіювати посилання"}
                    >
                      <BtnWrapper
                        $bgColor="var(--sidebar-hover)"
                        $width="20px"
                        onClick={onClickCopy}
                        onMouseEnter={onHoverCopy}
                        onMouseLeave={onCloseCopy}
                      >
                        <LinkIcon />
                      </BtnWrapper>
                    </Tooltip>
                  )}
                  <AlertTooltip title="Зберегти">
                    <span>
                      <DownloadButton
                        doc={doc}
                        selectedDocs={doc.cancelled ? "" : [doc.id]}
                        type={adminDoc ? "pub" : null}
                        bgColor="var(--sidebar-hover)"
                      />
                    </span>
                  </AlertTooltip>
                  {canDelete ? (
                    <DeleteButton
                      state={deleteDocState.state}
                      single
                      onClick={() => initiateDelete(doc)}
                      error={
                        deleteState === "failure" ? errorDelete : undefined
                      }
                    />
                  ) : null}

                  <ArchiveButton
                    doc={doc}
                    selectedDocs={doc.cancelled ? [] : [doc.id]}
                    bgColor="var(--sidebar-hover)"
                  />

                  {isMobile ? null : (
                    <div style={{ width: "24px" }}>
                      <ZoomIcon />
                    </div>
                  )}
                  {!isNarrow && (
                    <ScaleSlider
                      size={isMobile ? "small" : "normal"}
                      zoomIcon={!isMobile}
                      max={200}
                      scale={scale}
                      handleSliderChange={handleSliderChange}
                    />
                  )}
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                  {numPages > 1 && (
                    <>
                      <BtnWrapper
                        $bgColor="var(--sidebar-hover)"
                        $color="var(--blue)"
                        $width="36px"
                        $height="36px"
                        $widthMob="30px"
                        $heightMob="30px"
                        disabled={currentPage === 1}
                        onClick={() => goTo(currentPage - 1)}
                        style={{ fontSize: 18 }}
                      >
                        ‹
                      </BtnWrapper>
                      <span
                        style={{
                          fontSize: 14,
                          color: "var(--black)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {!isNarrow && "Сторінка "}<strong>{currentPage}</strong> з {numPages}
                      </span>
                      <BtnWrapper
                        $bgColor="var(--sidebar-hover)"
                        $color="var(--blue)"
                        $width="36px"
                        $height="36px"
                        $widthMob="30px"
                        $heightMob="30px"
                        disabled={currentPage === numPages}
                        onClick={() => goTo(currentPage + 1)}
                        style={{ fontSize: 18 }}
                      >
                        ›
                      </BtnWrapper>
                    </>
                  )}
                  </div>
                </>
              )}
              {isMobile && (
                <BtnWrapper $bgColor="var(--blue)">
                  <MoreVertIcon
                    sx={{ color: "white" }}
                    onClick={() => setShowDetails(true)}
                  />
                </BtnWrapper>
              )}
            </div>
            {isMobile ? (
              <div className={styles.blockStatus}>
                {doc.state === "loading" ? (
                  <SkeletonOneLine />
                ) : (
                  <>
                    <div>{StatusDocInfo(doc)}</div>
                    {!!doc.comment && <p>{doc.comment}</p>}
                    {doc.error && doc.error !== 115 && (
                      <p>{doc.error.message}</p>
                    )}
                  </>
                )}
              </div>
            ) : isMobile ? (
              <div style={{ height: "15px" }}></div>
            ) : null}
            {isNarrow && (
              <ScaleSlider
                size={isMobile ? "small" : "normal"}
                zoomIcon={!isMobile}
                max={200}
                scale={scale}
                handleSliderChange={handleSliderChange}
              />
            )}

            <div
              className={
                Object.keys(signMethods).length === 0
                  ? styles.pdf
                  : Object.keys(signMethods).length < 3 && !doc.error
                    ? `${styles.pdf} ${styles.pdf_m}`
                    : `${styles.pdf} ${styles.pdf_s}`
              }
              ref={componentRef}
            >
              <PdfViewer
                url={doc.url}
                width={width}
                height={height}
                scale={scale}
                currentPage={currentPage}
                onLoadSuccess={(n) => {
                  setNumPages(n);
                  setCurrentPage(1);
                }}
                onScaleChange={setScale}
              />
            </div>
          </div>
        )}
        {isMobile && doc.state !== "loading" && (
          <DocsToolbarDevice
            hideDownloadFilesBtn={true}
            showDetails={showDetails}
            signMethods={signMethods}
            sign={sign}
            doc={doc}
            onOpenAttrPopup={(hint) => {
              setAttrHint(hint);
              setEditAttrPopup(true);
            }}
          />
        )}
        {isMobile && adminDoc && doc.public && (
          <AdminDocCounterparties doc={doc} />
        )}

        {isDesktop && (
          <div className={styles.right} id="right">
            {doc.state === "loading" && !doc.id ? (
              <SkeletonInfobar />
            ) : (
              <>
                {/* {!adminDoc && !signed && (
                  <DocsToolbarDevice
                    signMethods={signMethods}
                    sign={sign}
                    doc={doc}
                  />
                )} */}

                <Infobar
                  doc={doc}
                  sign={sign}
                  signMethods={signMethods}
                  general={adminDoc}
                />
                {adminDoc && <AdminDocCounterparties doc={doc} />}
              </>
            )}
          </div>
        )}
      </section>
      {showPopup ? (
        <SignPopup
          clickCloseButton={closePopup}
          onSubmit={submitForm}
          sign={sign}
        />
      ) : null}
      {showDiiaPopup ? (
        <DiiaSignPopup clickCloseButton={closeDiiaPopup} diia={diia} />
      ) : null}
      {showFilePopup ? (
        <FileSignPopup
          clickCloseButton={closeFilePopup}
          onSubmit={submitForm}
          sign={sign}
        />
      ) : null}
      {editAttrPopup ? (
        <AddAttributesDocPopup
          handleClose={() => {
            setAttrHint(undefined);
            setEditAttrPopup(false);
          }}
          hint={attrHint}
          {...doc}
          edit
        />
      ) : null}
      {showConfirmModal ? (
        <DeleteConfirmModal
          onConfirm={confirmDelete}
          docs={docsToDeleteTitles}
          onClose={cancelDelete}
        />
      ) : null}
    </>
  );
}
